import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import NodeBlock from "./nodeblock";
import { buildLevels, buildEdges } from "../../utils/gerarchiautils";

const LEVEL_GAP = 75; 
const PAD = 5;        

export default function TreeCanvas({ all, root, onAddChildren, onRemove, handleLeafClick , 
    readOnly = false, showConversions = true, selectedLeafId = null, disabledLeafId = null }) {

  const containerRef = useRef(null);
  const nodeRefs = useRef({});     // tempId -> ref HTMLElement
  const [lines, setLines] = useState([]); // [{x1,y1,x2,y2}]
  const [size, setSize] = useState({ w: 0, h: 0 });

  const levels = useMemo(() => buildLevels(all, root), [all, root]);
  const edges  = useMemo(() => buildEdges(all), [all]);

  useLayoutEffect(() => {
    all.forEach((n) => {
      if (!nodeRefs.current[n.tempId]) nodeRefs.current[n.tempId] = React.createRef();
    });
  }, [all]);

  const measure = () => {
    const cont = containerRef.current;
    if (!cont) return;

    // copri tutta l'area contenuto
    const w = cont.scrollWidth;
    const h = cont.scrollHeight;
    if (w !== size.w || h !== size.h) setSize({ w, h });

    const cbox = cont.getBoundingClientRect();
    const pts = [];

    for (const e of edges) {
      const fromEl = nodeRefs.current[e.from]?.current;
      const toEl   = nodeRefs.current[e.to]?.current;
      if (!fromEl || !toEl) continue;

      const a = fromEl.getBoundingClientRect();
      const b = toEl.getBoundingClientRect();

      // dal bordo inferiore del padre (fuori dal box) al bordo superiore del figlio (fuori dal box)
      const x1 = a.left - cbox.left + a.width / 2;
      const y1 = a.top  - cbox.top  + a.height + PAD; // fuori dal padre
      const x2 = b.left - cbox.left + b.width  / 2;
      const y2 = b.top  - cbox.top  - PAD;            // fuori dal figlio

      pts.push({ x1, y1, x2, y2 });
    }
    setLines(pts);
  };

  useEffect(() => {
    measure();
    const onRsz = () => measure();
    window.addEventListener("resize", onRsz);

    const ro = new ResizeObserver(() => requestAnimationFrame(measure));
    if (containerRef.current) ro.observe(containerRef.current);
    Object.values(nodeRefs.current).forEach((r) => r?.current && ro.observe(r.current));

    const mo = new MutationObserver(() => requestAnimationFrame(measure));
    if (containerRef.current) mo.observe(containerRef.current, { childList: true, subtree: true });

    const t = setTimeout(measure, 0);
    return () => {
      window.removeEventListener("resize", onRsz);
      ro.disconnect();
      mo.disconnect();
      clearTimeout(t);
    };
  }, [levels, edges]);

  return (
    <div ref={containerRef} style={{ position: "relative"}}>
      {!readOnly && (
      <p>Clicca sopra i <span style={{ fontWeight: "bold", color: "blue" }}>riquadri Blu (raggruppamenti di servizi)</span> per aggiungere le sue categorie figlie.</p>
      )}
      {/* SVG overlay per frecce dritte */}
      <svg
        style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0 }}
        width={size.w}
        height={size.h}
        viewBox={`0 0 ${size.w} ${size.h}`}
      >
        <defs>
          <marker id="arrow" markerWidth="10" markerHeight="10" refX="10" refY="5" orient="auto">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#999" />
          </marker>
        </defs>
        {lines.map((p, i) => (
          <line
            key={i}
            x1={p.x1}
            y1={p.y1}
            x2={p.x2}
            y2={p.y2}
            stroke="#999"
            strokeWidth="2"
            markerEnd="url(#arrow)"
            strokeLinecap="square"
          />
        ))}
      </svg>

      {/* Livelli centrati; più distanti verticalmente */}
      {levels.map((row, li) => (
        <div
          key={li}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start", // altezze diverse tra fratelli non spostano l'ancoraggio
            gap: 16,
            marginTop: li === 0 ? 0 : LEVEL_GAP,
            position: "relative",
            zIndex: 1, // i blocchi stanno sopra le linee
          }}
        >
          {row.map((node) => {
            const ref = nodeRefs.current[node.tempId];
            return (
              <div key={node.tempId} ref={ref} style={{ position: "relative" }}>
                <NodeBlock
                  all={all}
                  node={node}
                  onAddChildren={onAddChildren}
                  onRemove={onRemove}
                  readOnly={readOnly}  
                  onLeafClick={handleLeafClick}
                  showConversions={showConversions}
                  selectedLeafId={selectedLeafId}
                  disabledLeafId={disabledLeafId}
                />
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
